/**
 * First Glance: Black–Scholes closed-form checks vs chain quotes, GBM Monte Carlo ranges,
 * and Greek-based narration for IV (width), gamma (local curvature / pin), delta (ATM strength).
 */

import type { ScannerData } from '../types/ScannerData';
import { normalizeScannerExpiryToIso } from './firstGlanceSpotGammaInsight';

const DEFAULT_R = 0.052; // annual, for BS / GBM drift (display-only)

/** Error function approximation (Abramowitz & Stegun). */
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y = 1 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax));
  return sign * y;
}

export function normalCDF(x: number): number {
  if (!Number.isFinite(x)) return 0;
  return 0.5 * (1 + erf(x / Math.SQRT2));
}

export function bsD1D2(S: number, K: number, T: number, r: number, sigma: number): { d1: number; d2: number } {
  if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) return { d1: 0, d2: 0 };
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  return { d1, d2 };
}

export function bsCallPrice(S: number, K: number, T: number, r: number, sigma: number): number {
  const { d1, d2 } = bsD1D2(S, K, T, r, sigma);
  if (T <= 0 || sigma <= 0) return Math.max(S - K, 0);
  return S * normalCDF(d1) - K * Math.exp(-r * T) * normalCDF(d2);
}

export function bsPutPrice(S: number, K: number, T: number, r: number, sigma: number): number {
  const { d1, d2 } = bsD1D2(S, K, T, r, sigma);
  if (T <= 0 || sigma <= 0) return Math.max(K - S, 0);
  return K * Math.exp(-r * T) * normalCDF(-d2) - S * normalCDF(-d1);
}

export function bsCallDelta(S: number, K: number, T: number, r: number, sigma: number): number {
  const { d1 } = bsD1D2(S, K, T, r, sigma);
  return normalCDF(d1);
}

export function bsGamma(S: number, K: number, T: number, r: number, sigma: number): number {
  if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) return 0;
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const n = Math.exp(-0.5 * d1 * d1) / Math.sqrt(2 * Math.PI);
  return n / (S * sigma * sqrtT);
}

export function bsVega(S: number, K: number, T: number, r: number, sigma: number): number {
  if (S <= 0 || K <= 0 || T <= 0 || sigma <= 0) return 0;
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const n = Math.exp(-0.5 * d1 * d1) / Math.sqrt(2 * Math.PI);
  return S * n * sqrtT;
}

/** Risk-neutral terminal lognormal: P(low < S_T < high). */
export function lognormalTerminalProbInRange(
  S0: number,
  low: number,
  high: number,
  T: number,
  r: number,
  sigma: number
): number {
  if (S0 <= 0 || T <= 0 || sigma <= 0 || !Number.isFinite(high) || !Number.isFinite(low)) return 0;
  if (high <= low) return 0;
  const mu = (r - 0.5 * sigma * sigma) * T;
  const sd = sigma * Math.sqrt(T);
  if (sd <= 0) return 0;
  const lnS = Math.log(S0);
  const zLo = (Math.log(Math.max(low, 1e-12)) - lnS - mu) / sd;
  const zHi = (Math.log(high) - lnS - mu) / sd;
  return Math.max(0, Math.min(1, normalCDF(zHi) - normalCDF(zLo)));
}

function readNumeric(row: ScannerData | undefined, keys: string[]): number | undefined {
  if (!row) return undefined;
  for (const k of keys) {
    const v = (row as unknown as Record<string, unknown>)[k];
    if (v === undefined || v === null || v === '' || v === '-') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function optionSide(row: ScannerData): 'C' | 'P' | null {
  const t = String(row['Option Type'] ?? row.optionType ?? row.option_type ?? row.Side ?? '').toUpperCase();
  if (t === 'C' || t === 'CALL') return 'C';
  if (t === 'P' || t === 'PUT') return 'P';
  return null;
}

export type AtmStrikeSnapshot = {
  strike: number;
  call?: ScannerData;
  put?: ScannerData;
};

/** Nearest strike to spot on the expiration; paired call/put rows when both exist. */
export function findAtmStrikeSnapshot(
  rows: ScannerData[] | undefined,
  expiryIso: string,
  symbolUpper: string,
  spot: number
): AtmStrikeSnapshot | null {
  if (!rows?.length || !expiryIso.trim() || !Number.isFinite(spot) || spot <= 0) return null;
  const sym = symbolUpper.trim().toUpperCase();
  const byStrike = new Map<number, { C?: ScannerData; P?: ScannerData }>();
  for (const r of rows) {
    const rawExp = String(r['Expiry Date'] || '').trim();
    if (!rawExp || normalizeScannerExpiryToIso(rawExp) !== expiryIso) continue;
    if (sym) {
      const u = String(r.Underlying || r.underlyingSymbol || '').trim().toUpperCase();
      if (u && u !== sym) continue;
    }
    const k = Number(r.Strike);
    if (!Number.isFinite(k)) continue;
    const side = optionSide(r);
    if (!side) continue;
    const cur = byStrike.get(k) || {};
    if (side === 'C') cur.C = r;
    else cur.P = r;
    byStrike.set(k, cur);
  }
  if (!byStrike.size) return null;
  let bestK = 0;
  let bestD = Infinity;
  for (const k of Array.from(byStrike.keys())) {
    const d = Math.abs(k - spot);
    if (d < bestD) {
      bestD = d;
      bestK = k;
    }
  }
  const pair = byStrike.get(bestK)!;
  return { strike: bestK, call: pair.C, put: pair.P };
}

export function readOptionMidOrLast(row: ScannerData | undefined): number | undefined {
  if (!row) return undefined;
  const mid = readNumeric(row, ['Mid', 'mid']);
  const last = readNumeric(row, ['Last', 'last']);
  if (mid != null && mid > 0) return mid;
  if (last != null && last > 0) return last;
  return undefined;
}

/** Heuristic 0–1: 1 = call Δ near 0.5 (true ATM in BS sense). */
export function atmDeltaStrengthScore(callDeltaAbs: number | undefined): number | undefined {
  if (callDeltaAbs == null || !Number.isFinite(callDeltaAbs)) return undefined;
  const d = Math.abs(Math.abs(callDeltaAbs) - 0.5);
  return Math.max(0, Math.min(1, 1 - 2 * d));
}

export type GreekRangeGuidance = {
  ivNarrative: string;
  gammaNarrative: string;
  deltaNarrative: string;
  /** BS risk-neutral prob terminal spot lands inside displayed band (same σ as model IV). */
  bsTerminalProbInDisplayedBand: number | null;
  chainCallDelta: number | null;
  chainPutDelta: number | null;
  chainCallGamma: number | null;
  atmStrengthScore: number | null;
};

export function buildGreekRangeGuidance(params: {
  modelIvPercent: number;
  rangeLow: number;
  rangeHigh: number;
  spot: number;
  T: number;
  r?: number;
  atmSnapshot: AtmStrikeSnapshot | null;
}): GreekRangeGuidance {
  const r = params.r ?? DEFAULT_R;
  const iv = params.modelIvPercent;
  const sigma = iv > 0 ? (iv >= 1 ? iv / 100 : iv) : 0;

  const ivNarrative =
    iv > 0
      ? 'Implied volatility scales the width of move bands (√time). Higher IV → wider ranges before Greeks adjust interpretation.'
      : 'Load ATM implied volatility to anchor width. IV enters σ in σ√T for diffusion-style bands.';

  const call = params.atmSnapshot?.call;
  const put = params.atmSnapshot?.put;
  const gChain = readNumeric(call, ['Gamma', 'gamma']) ?? readNumeric(put, ['Gamma', 'gamma']);
  const dCall = readNumeric(call, ['Delta', 'delta']);
  const dPut = readNumeric(put, ['Delta', 'delta']);
  const dForScore =
    dCall != null && Number.isFinite(dCall) ? dCall : dPut != null && Number.isFinite(dPut) ? Math.abs(dPut) : undefined;

  let gammaNarrative =
    'Gamma is highest near ATM: it measures how fast Δ moves per $1 in spot—local curvature. High γ → pinning pressure and path-dependent outcomes; pure lognormal hit-probabilities can understate wings.';
  if (gChain != null && gChain > 0) {
    gammaNarrative += ` Chain ATM option γ ≈ ${gChain.toExponential(2)} — use MC or BS check when γ is elevated.`;
  }

  const score = atmDeltaStrengthScore(dForScore);
  let deltaNarrative =
    '|Δ| near 0.5 at the chosen strike means the chain is centered on ATM; IV-based bands are a stronger local approximation. Away from 0.5, the “ATM IV” row may be slightly off-strike.';
  if (dCall != null && Number.isFinite(dCall)) {
    deltaNarrative += ` Observed call Δ ≈ ${dCall.toFixed(3)} (strength ${score != null ? (score * 100).toFixed(0) : '—'}%).`;
  } else if (dPut != null && Number.isFinite(dPut)) {
    deltaNarrative += ` Observed put Δ ≈ ${dPut.toFixed(3)} (|Δ| strength ${score != null ? (score * 100).toFixed(0) : '—'}%).`;
  }

  let bsTerminalProbInDisplayedBand: number | null = null;
  if (sigma > 0 && params.T > 0 && params.spot > 0) {
    bsTerminalProbInDisplayedBand = lognormalTerminalProbInRange(
      params.spot,
      params.rangeLow,
      params.rangeHigh,
      params.T,
      r,
      sigma
    );
  }

  return {
    ivNarrative,
    gammaNarrative,
    deltaNarrative,
    bsTerminalProbInDisplayedBand,
    chainCallDelta: dCall ?? null,
    chainPutDelta: dPut ?? null,
    chainCallGamma: gChain ?? null,
    atmStrengthScore: score ?? null,
  };
}

export type BlackScholesCompareResult = {
  spot: number;
  strike: number;
  T: number;
  r: number;
  sigmaModel: number;
  callTheoretical: number;
  putTheoretical: number;
  callMarket?: number;
  putMarket?: number;
  callDiffPct?: number;
  putDiffPct?: number;
  impliedSigmaFromCall?: number;
  deltaCall: number;
  gamma: number;
  vega: number;
  probAboveStrikeAtT: number;
  terminalProbInDisplayedBand: number;
  notes: string[];
};

function impliedVolBisection(
  S: number,
  K: number,
  T: number,
  r: number,
  market: number,
  isCall: boolean,
  lo = 0.01,
  hi = 3.0,
  iters = 80
): number | undefined {
  if (S <= 0 || K <= 0 || T <= 0 || market <= 0) return undefined;
  const price = (sig: number) => (isCall ? bsCallPrice(S, K, T, r, sig) : bsPutPrice(S, K, T, r, sig));
  let a = lo;
  let b = hi;
  let fa = price(a) - market;
  let fb = price(b) - market;
  if (fa * fb > 0) return undefined;
  for (let i = 0; i < iters; i++) {
    const m = 0.5 * (a + b);
    const fm = price(m) - market;
    if (Math.abs(fm) < 1e-6) return m;
    if (fa * fm <= 0) {
      b = m;
      fb = fm;
    } else {
      a = m;
      fa = fm;
    }
  }
  return 0.5 * (a + b);
}

export function runBlackScholesCompare(params: {
  spot: number;
  strike: number;
  T: number;
  modelIvPercent: number;
  rangeLow: number;
  rangeHigh: number;
  r?: number;
  callMarket?: number;
  putMarket?: number;
}): BlackScholesCompareResult | null {
  const r = params.r ?? DEFAULT_R;
  const S = params.spot;
  const K = params.strike;
  const T = params.T;
  if (S <= 0 || K <= 0 || T <= 0) return null;
  const ivp = params.modelIvPercent;
  const sigma = ivp >= 1 ? ivp / 100 : ivp;
  if (sigma <= 0) return null;

  const callTheoretical = bsCallPrice(S, K, T, r, sigma);
  const putTheoretical = bsPutPrice(S, K, T, r, sigma);
  const { d2 } = bsD1D2(S, K, T, r, sigma);
  const probAboveStrikeAtT = normalCDF(d2);
  const terminalProbInDisplayedBand = lognormalTerminalProbInRange(
    S,
    params.rangeLow,
    params.rangeHigh,
    T,
    r,
    sigma
  );

  const deltaCall = bsCallDelta(S, K, T, r, sigma);
  const gamma = bsGamma(S, K, T, r, sigma);
  const vega = bsVega(S, K, T, r, sigma);

  let callDiffPct: number | undefined;
  let putDiffPct: number | undefined;
  if (params.callMarket != null && params.callMarket > 0) {
    callDiffPct = ((callTheoretical - params.callMarket) / params.callMarket) * 100;
  }
  if (params.putMarket != null && params.putMarket > 0) {
    putDiffPct = ((putTheoretical - params.putMarket) / params.putMarket) * 100;
  }

  let impliedSigmaFromCall: number | undefined;
  if (params.callMarket != null && params.callMarket > 0) {
    impliedSigmaFromCall = impliedVolBisection(S, K, T, r, params.callMarket, true);
  }

  const notes: string[] = [];
  notes.push(`BS uses r=${(r * 100).toFixed(2)}% p.a. and the model ATM IV σ=${(sigma * 100).toFixed(2)}%.`);
  if (impliedSigmaFromCall != null) {
    const diffIv = (impliedSigmaFromCall - sigma) * 100;
    notes.push(
      `Implied σ from call mid ≈ ${(impliedSigmaFromCall * 100).toFixed(2)}% (${diffIv >= 0 ? '+' : ''}${diffIv.toFixed(2)}% vs model IV).`
    );
  }
  notes.push(
    `Risk-neutral P(S_T > K) ≈ ${(probAboveStrikeAtT * 100).toFixed(1)}% at this strike (BS d₂).`
  );

  return {
    spot: S,
    strike: K,
    T,
    r,
    sigmaModel: sigma,
    callTheoretical,
    putTheoretical,
    callMarket: params.callMarket,
    putMarket: params.putMarket,
    callDiffPct,
    putDiffPct,
    impliedSigmaFromCall,
    deltaCall,
    gamma,
    vega,
    probAboveStrikeAtT,
    terminalProbInDisplayedBand,
    notes,
  };
}

export type MonteCarloRangeResult = {
  paths: number;
  seed: number;
  T: number;
  sigma: number;
  r: number;
  p05: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
  mean: number;
  std: number;
  fracInDisplayedBand: number;
};

function mulberry32(a: number) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** GBM terminal prices: S_T = S0 exp((r-σ²/2)T + σ√T Z). */
export function runMonteCarloGBM(params: {
  S0: number;
  T: number;
  sigma: number;
  r?: number;
  nPaths: number;
  seed: number;
  rangeLow: number;
  rangeHigh: number;
}): MonteCarloRangeResult | null {
  const { S0, T, rangeLow, rangeHigh } = params;
  const sigma = params.sigma >= 1 ? params.sigma / 100 : params.sigma;
  const r = params.r ?? DEFAULT_R;
  const nPaths = Math.max(1000, Math.min(100_000, Math.floor(params.nPaths)));
  if (S0 <= 0 || T <= 0 || sigma <= 0) return null;

  const rnd = mulberry32(params.seed >>> 0);
  const drift = (r - 0.5 * sigma * sigma) * T;
  const vol = sigma * Math.sqrt(T);
  const terminals: number[] = new Array(nPaths);
  let sum = 0;
  let sumSq = 0;
  let inside = 0;

  for (let i = 0; i < nPaths; i++) {
    const u1 = Math.max(1e-12, rnd());
    const u2 = Math.max(1e-12, rnd());
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const st = S0 * Math.exp(drift + vol * z);
    terminals[i] = st;
    sum += st;
    sumSq += st * st;
    if (st >= rangeLow && st <= rangeHigh) inside++;
  }

  terminals.sort((a, b) => a - b);
  const q = (p: number) => terminals[Math.min(nPaths - 1, Math.max(0, Math.floor(p * (nPaths - 1))))];

  const mean = sum / nPaths;
  const var_ = Math.max(0, sumSq / nPaths - mean * mean);
  const std = Math.sqrt(var_);

  return {
    paths: nPaths,
    seed: params.seed >>> 0,
    T,
    sigma,
    r,
    p05: q(0.05),
    p25: q(0.25),
    p50: q(0.5),
    p75: q(0.75),
    p95: q(0.95),
    mean,
    std,
    fracInDisplayedBand: inside / nPaths,
  };
}
